'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import BrandLabScene from './BrandLabScene';

const BrandLabHero = () => {
    // --- Dynamic Data Simulation State ---
    const [rankings, setRankings] = useState([
        { id: 1, label: 'SEO Specialist', rank: 2 },
        { id: 2, label: 'Brand Expert', rank: 5 },
        { id: 3, label: 'Local Search', rank: 8 },
    ]);

    const [gmbStats, setGmbStats] = useState({
        score: 96,
        calls: 24,
        views: 45,
        clicks: 18
    });

    const [currentReview, setCurrentReview] = useState("Excellent Service!");
    const [isReviewVisible, setIsReviewVisible] = useState(true);
    const reviewOptions = ["Excellent Service!", "Top Rated Advice", "Fast Results", "Highly Recommended", "Trustworthy Team"];

    // Data Simulation Effect
    useEffect(() => {
        const interval = setInterval(() => {
            // 1. Update Rankings
            setRankings(prev => {
                const next = prev.map(item => {
                    if (item.rank > 1 && Math.random() > 0.6) {
                        return { ...item, rank: item.rank - 1 };
                    }
                    return item;
                });
                return [...next].sort((a, b) => a.rank - b.rank);
            });

            // 2. Update Stats
            setGmbStats(prev => ({
                score: Math.min(100, prev.score + (Math.random() > 0.8 ? 1 : 0)),
                calls: prev.calls + (Math.random() > 0.7 ? 1 : 0),
                views: prev.views + (Math.random() > 0.5 ? 1 : 0),
                clicks: prev.clicks + (Math.random() > 0.8 ? 1 : 0),
            }));
        }, 2000);

        // Separate interval for Review Switching with Fade
        const reviewInterval = setInterval(() => {
            setIsReviewVisible(false); // Fade Out
            setTimeout(() => {
                setCurrentReview(prev => {
                    const idx = reviewOptions.indexOf(prev);
                    return reviewOptions[(idx + 1) % reviewOptions.length];
                });
                setIsReviewVisible(true); // Fade In
            }, 300); // Wait for fade out
        }, 3000); // 3 Seconds

        return () => {
            clearInterval(interval);
            clearInterval(reviewInterval);
        };
    }, []);

    return (
        <section className="relative w-full pt-40 pb-20 lg:pt-48 lg:pb-32 px-6 overflow-hidden min-h-screen flex flex-col bg-white">

            {/* 3D Background Container */}
            <div className="absolute inset-y-0 right-0 w-full lg:w-3/5 z-0 pointer-events-none opacity-100" style={{ maskImage: 'linear-gradient(to right, transparent, black 15%)' }}>
                <BrandLabScene />
            </div>

            <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-16 items-center w-full">

                {/* Left: Content */}
                <div className="flex flex-col gap-8 text-center lg:text-left order-2 lg:order-1">



                    <h1 className="text-5xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-[1.1]">
                        Elevate Your <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 animate-gradient-x bg-[length:200%_auto]">
                            Brand Visibility
                        </span>
                        <br />
                        <span className="font-serif italic font-light text-slate-600">with Proven SEO</span>
                    </h1>

                    <p className="text-lg text-slate-600 max-w-xl mx-auto lg:mx-0 leading-relaxed font-light">
                        Transform your online presence with <strong>our team's</strong> insider knowledge of Google Business Profile optimization and strategic SEO management. Get measurable results that drive real business growth.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mt-4">
                        <Link
                            href="/contact"
                            className="w-full sm:w-auto px-8 py-4 bg-slate-900 hover:bg-indigo-600 text-white rounded-full font-semibold transition-all duration-300 transform hover:-translate-y-1 shadow-xl shadow-indigo-900/20 flex items-center justify-center gap-2 group"
                        >
                            Book Free Brand Audit
                            <Icon name="ArrowRightIcon" className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            href="#measurable-results"
                            className="w-full sm:w-auto px-8 py-4 bg-white border border-slate-200 hover:border-indigo-200 hover:bg-indigo-50 text-slate-900 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            View Success Stories
                        </Link>
                    </div>

                    {/* Stats Section */}
                    <div className="mt-10 pt-8 border-t border-slate-100 flex flex-wrap justify-center lg:justify-start gap-12">
                        <div>
                            <div className="text-3xl font-bold text-slate-900">50+</div>
                            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mt-1">Brands Optimized</div>
                        </div>
                        <div>
                            <div className="text-3xl font-bold text-slate-900">3.5x</div>
                            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mt-1">Traffic Growth</div>
                        </div>
                        <div>
                            <div className="text-3xl font-bold text-slate-900">92%</div>
                            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mt-1">Retention</div>
                        </div>
                    </div>
                </div>

                {/* Right: Visuals (Floating Cards showing SKILLS) */}
                <div className="relative h-[500px] flex items-center justify-center order-1 lg:order-2 perspective-1000">

                    {/* Card 1: Ranking Booster */}
                    <div
                        className="absolute top-12 right-0 lg:-right-8 w-72 bg-white/90 backdrop-blur-xl p-5 rounded-2xl shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] border border-white z-20 animate-float-slow"
                    >
                        <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-3">
                            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
                                <Icon name="ChartBarIcon" className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-slate-900">Ranking Surge</h3>
                                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Live Updates</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {rankings.map((item) => (
                                <div key={item.id} className="flex items-center justify-between">
                                    <span className="text-sm text-slate-600 font-medium">{item.label}</span>
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1 transition-all duration-300 ${item.rank === 1 ? 'text-green-600 bg-green-50' : 'text-slate-600 bg-slate-100'}`}>
                                        #{item.rank} <span className="text-[10px] text-green-500">▲</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Card 2: Profile Optimization */}
                    <div
                        className="absolute bottom-24 left-0 lg:left-6 w-64 bg-white/90 backdrop-blur-xl p-5 rounded-2xl shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] border border-white z-20 animate-float-delayed"
                    >
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <h4 className="font-bold text-slate-900">GMB Health</h4>
                                <p className="text-xs text-slate-500">Profile Optimization</p>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-1 rounded-full transition-colors duration-500 ${gmbStats.score >= 100 ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`}>
                                {gmbStats.score >= 100 ? 'OPTIMIZED' : 'ACTIVE'}
                            </span>
                        </div>

                        <div className="flex items-end gap-2 mt-2 mb-1">
                            <span className="text-4xl font-bold text-slate-900 tracking-tight">{Math.floor(gmbStats.score)}%</span>
                            <span className="text-xs text-slate-400 font-medium mb-1.5">Optimized</span>
                        </div>

                        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden mb-3">
                            <div
                                className="bg-green-500 h-full rounded-full transition-all duration-1000 shadow-[0_0_10px_rgba(34,197,94,0.4)]"
                                style={{ width: `${gmbStats.score}%` }}
                            ></div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-3">
                            <div className="bg-slate-50 rounded p-1.5 text-center">
                                <div className="text-[10px] text-slate-400">Calls</div>
                                <div className="text-xs font-bold text-slate-700">↑ {gmbStats.calls}%</div>
                            </div>
                            <div className="bg-slate-50 rounded p-1.5 text-center">
                                <div className="text-[10px] text-slate-400">Views</div>
                                <div className="text-xs font-bold text-slate-700">↑ {gmbStats.views}%</div>
                            </div>
                            <div className="bg-slate-50 rounded p-1.5 text-center">
                                <div className="text-[10px] text-slate-400">Clicks</div>
                                <div className="text-xs font-bold text-slate-700">↑ {gmbStats.clicks}%</div>
                            </div>
                        </div>
                    </div>

                    {/* Card 3: Review Sentiment Stream (Option 4) */}
                    <div className="absolute bottom-10 right-0 lg:-right-4 w-60 bg-white/90 backdrop-blur-xl p-4 rounded-xl shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] border border-white z-30 animate-float-medium hidden lg:block">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600">
                                    <Icon name="StarIcon" className="w-3.5 h-3.5 fill-current" />
                                </div>
                                <span className="text-xs font-bold text-slate-900">Sentiment</span>
                            </div>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-50 text-green-700 rounded border border-green-100">
                                Trust 5.0
                            </span>
                        </div>

                        <div className="relative h-12 overflow-hidden">
                            {/* Animated Comment Stream */}
                            <div className={`absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-300 transform ${isReviewVisible ? 'opacity-100' : 'opacity-0'}`}>
                                <div className="text-sm font-medium text-slate-800 text-center italic">"{currentReview}"</div>
                            </div>
                        </div>

                        <div className="flex gap-0.5 mt-2 justify-center">
                            {[1, 2, 3, 4, 5].map(i => (
                                <Icon key={i} name="StarIcon" className="w-3 h-3 text-yellow-400 fill-current" />
                            ))}
                        </div>
                    </div>

                </div>

            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40 hover:opacity-100 transition-opacity cursor-pointer">
                <span className="text-[10px] font-bold tracking-[0.2em] text-slate-400 uppercase">Scroll Down</span>
                <Icon name="ChevronDownIcon" className="w-4 h-4 text-slate-400 animate-bounce" />
            </div>

            <style>{`
            @keyframes float-slow {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-12px); }
            }
            @keyframes float-delayed {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-15px); }
            }
            .animate-float-slow {
              animation: float-slow 6s ease-in-out infinite;
            }
            .animate-float-delayed {
              animation: float-delayed 7s ease-in-out infinite 1.5s;
            }
            .animate-float-medium {
              animation: float-slow 5s ease-in-out infinite 1s; /* Slightly faster than slow */
            }
            .perspective-1000 {
              perspective: 1000px;
            }
          `}</style>
        </section>
    );
};

export default BrandLabHero;
